import { useState } from 'react';

import { useAuth } from '../../../global/contexts/auth/auth';
import { avatarTone, initialsOf } from '../../../common/libs/initials';
import { ROLE_OPTIONS } from '../../users/utils/form';
import ChangePasswordForm from '../forms/password';

import '../styles/profile.css';
import '../../clients/styles/form.css';

// My own account.
//
// READ ONLY except for the password, and that is the contract's doing, not a
// decision taken here: the only route that writes a user is
// PATCH /admin/users/{id}, which demands `users:write`. A technician editing
// their own name would be answered 403. Showing an editable field that the API
// refuses is worse than showing the value.
//
// Everything on screen comes from /auth/me, which AuthProvider already holds —
// no request of its own.

const ROLE_LABEL = Object.fromEntries(ROLE_OPTIONS.map(o => [o.value, o.label]));
const ROLE_HINT = Object.fromEntries(ROLE_OPTIONS.map(o => [o.value, o.hint]));

const STATUS_LABEL = { 2: 'Activa', 1: 'Inactiva', 0: 'Eliminada' };

const DATE_FORMAT = new Intl.DateTimeFormat('es', {
    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

function formatDate(raw) {
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : DATE_FORMAT.format(date);
}

export default function Profile() {
    const { user } = useAuth();
    const [changing, setChanging] = useState(false);

    if (!user) return <p className="profile__loading">Cargando tu cuenta...</p>;

    const lastLogin = formatDate(user.last_login_at ?? user.lastLoginAt);
    const since = formatDate(user.created_at ?? user.createdAt);

    return (
        <section className="client-form profile">
            <header className="profile__head">
                <span
                    className={`avatar profile__avatar avatar--tone-${avatarTone(user.name || user.email)}`}
                    aria-hidden="true"
                >
                    {initialsOf(user.name || user.email)}
                </span>

                <span className="profile__identity">
                    <h2 className="profile__name">{user.name}</h2>
                    <span className="profile__email">{user.email}</span>
                </span>
            </header>

            <div className="client-form__card">
                <div className="client-form__section">
                    <h3 className="client-form__section-title">Tu cuenta</h3>
                    <p className="client-form__section-text">
                        Estos datos los edita quien administra usuarios, no vos.
                    </p>
                </div>

                <dl className="profile__facts">
                    <div className="profile__fact">
                        <dt>Rol</dt>
                        <dd>
                            {ROLE_LABEL[user.role] ?? user.role}
                            {ROLE_HINT[user.role] && (
                                <span className="profile__hint">{ROLE_HINT[user.role]}</span>
                            )}
                        </dd>
                    </div>

                    <div className="profile__fact">
                        <dt>Estado</dt>
                        <dd>{STATUS_LABEL[user.status] ?? 'Desconocido'}</dd>
                    </div>

                    <div className="profile__fact">
                        <dt>Último ingreso</dt>
                        <dd>{lastLogin || 'Este es el primero'}</dd>
                    </div>

                    <div className="profile__fact">
                        <dt>Cuenta creada</dt>
                        <dd>{since || '—'}</dd>
                    </div>
                </dl>
            </div>

            <div className="client-form__card client-form__card--stacked">
                {/* An option and not a form standing open. Changing a password is
                    something somebody decides to do, not the last thing on their
                    account screen asking to be filled in. */}
                {!changing
                    ? (
                        <div className="profile__action">
                            <span className="profile__action-text">
                                <span className="client-form__section-title">Contraseña</span>
                                <span className="client-form__section-text">
                                    Solo vos podés cambiar la tuya.
                                </span>
                            </span>
                            <button
                                type="button"
                                className="profile__action-button"
                                onClick={() => setChanging(true)}
                            >
                                Cambiar contraseña
                            </button>
                        </div>
                    )
                    : (
                        <>
                            <ChangePasswordForm onDone={() => setChanging(false)} />
                            <div className="profile__action-cancel">
                                <button
                                    type="button"
                                    className="client-form__back"
                                    onClick={() => setChanging(false)}
                                >
                                    Cancelar
                                </button>
                            </div>
                        </>
                    )}
            </div>
        </section>
    );
}
